import Link from "next/link"
import Image from "next/image";
import Head from "next/head";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
    TooltipArrow
} from "@/components/tooltip"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/popover"
import mentors from "@/constatns/mentors"
import ChevronDown from "../../../../public/icons/chevronDown.svg"
import WorkIcon from "../../../../public/icons/work.svg";
import LocationIcon from "../../../../public/icons/location.svg";
import QuestionIcon from "../../../../public/icons/question.svg";
import LinkedInIcon from "../../../../public/icons/linkedin.svg";
import TwitterIcon from "../../../../public/icons/twitter.svg";
import InfoIcon from "../../../../public/icons/info.svg";
import type { InferGetServerSidePropsType, GetServerSideProps } from 'next'
import React from "react";

export const getServerSideProps: GetServerSideProps<{ name: string }> = async (context) => {
    const name = Array.isArray(context.params?.slug) ? context.params.slug[0] : context.params?.slug || '';
    const mentor = mentors.filter(mentor => mentor.fullName.toLowerCase() === name.split("-").join(" "))
    if (!mentor.length) {
        return {
            redirect: {
                destination: '/mentors',
                permanent: false,
            },
        };
    }

    return { props: { name } };
}

export default function Index({ name, }: InferGetServerSidePropsType<typeof getServerSideProps>) {
    const fullName = name?.split("-").map((item: string) => item[0].toUpperCase() + item.slice(1)).join(" ")

    const mentor = mentors.filter((mentor) => mentor.fullName === fullName)[0];
    const mentorSocialsLink = Object.values(mentor.social)
    const mentorSocialsName = Object.keys(mentor.social);

    let similarMentors;
    const topic = mentor.background.expertise

    similarMentors = mentors.filter(mentor => mentor.background.expertise === topic && mentor.fullName !== fullName);

    return (
        <main className="min-h-screen pt-6 pb-[120px]">
            <Head>
                <title>{name}</title>
                <link rel="canonical" href={`https://expatpie.com/mentor/${name}`} />
                <meta property="og:title" content={fullName} />
                <meta property="og:site_name" content="Expat Pie" />
                <meta property="og:description" content={`Learn more about ${name}, our experienced ${mentor.jobTitle} mentor with expertise in ${mentor.topics.join(",")}. Get insights into their background and how they can help you achieve your career goals.`} />
                <meta name="description" content={`Learn more about ${name}, our experienced ${mentor.jobTitle} mentor with expertise in ${mentor.topics.join(",")}. Get insights into their background and how they can help you achieve your career goals.`} />
                <meta property="og:image"
                    content={`https://expatpie.com/images/mentor/${name}.jpg`} />
                <meta property="og:url" content={`https://expatpie.com/mentor/${name}`} />
                <meta property="og:profile" content="website" />
            </Head>
            <div className="flex flex-col items-center relative">
                <div className="lg:w-4/5 lg:max-w-[1103px] px-9 z-50">
                    <ul className="flex items-center">
                        <li className="text-white-50 text-xs font-semibold">
                            <Link href="/">
                                Home
                            </Link>
                        </li>
                        <ChevronDown className="mx-2 -rotate-90" />
                        <li className="text-white-50 text-xs font-semibold">
                            <Link href="/mentors">
                                Mentors
                            </Link>
                        </li>
                        <ChevronDown className="mx-2 -rotate-90" />
                        <li className="text-white-68 text-xs font-semibold">
                            {mentor.fullName}
                        </li>
                    </ul>
                    <div className="flex flex-col items-center lg:items-start lg:flex-row mt-12 mb-16">
                        <Image
                            src={`/images/mentors/${name}.jpg`}
                            alt="mentor"
                            className="lg:mr-16 lg:min-w-[258px]"
                            width={258}
                            height={331}
                            quality={100}
                            placeholder="blur"
                            loading="lazy"
                            blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mMUqwcAALEAlw+6NSgAAAAASUVORK5CYII="
                        />
                        <div className="mt-6 lg:mt-0 lg:ml-2.5 flex flex-col lg:justify-between">
                            <div>
                                <h1 className="text-42 lg:text-5xl text-white font-semibold font-barlow">{mentor.fullName}</h1>
                                <h2 className="text-white-68 font-normal text-32 font-barlow">{mentor.jobTitle}</h2>
                                <div className="mt-8">
                                    <div className="flex items-center">
                                        <WorkIcon className="w-5 h-5" />
                                        <p className="ml-2 text-white-68 text-lg font-normal">working at
                                            <span className="text-white-100 ml-2">{mentor.company}</span>
                                        </p>
                                    </div>
                                    <div className="flex items-center mt-4">
                                        <LocationIcon className="w-5 h-5" />
                                        <p className="ml-2 text-white-68 text-lg font-normal">currently working in
                                            <span className="text-white-100 ml-2">{mentor.location.join(" & ")}</span>
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-16">
                                    <ul className="flex gap-2 flex-wrap">
                                        {mentor.topics.map((topic: string, index: number) => <li
                                            className="border border-white-30 px-2 py-2.5 text-white text-sm font-medium text-nowrap"
                                            key={index}>{topic}</li>)}
                                    </ul>
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center mt-11 gap-6">
                                <Link href={`/mentor/${name}/request`}>
                                    <button className="py-2.5 px-4 text-white font-semibold text-base bg-Accent-100">
                                        Request a session
                                    </button>
                                </Link>
                                <p className="text-white text-base font-normal">
                                    Pricing Upon Inquiry
                                </p>
                                {/* <TooltipProvider delayDuration={100}>
                                    <Tooltip>
                                        <TooltipTrigger>
                                            <div
                                                className="hidden lg:flex items-center px-4 py-2.5 cursor-pointer border border-black bg-darker">
                                                <p className="text-white text-base font-semibold mr-1">Fee quote </p>
                                                <QuestionIcon/>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <TooltipArrow width={12} height={12} className="fill-darker"/>
                                            <div
                                                className="px-4 py-1 bg-blur-100 flex border rounded-sm border-white-12 -mt-1.5">
                                                <div className="">
                                                    <p className="border-b border-white-12 text-base font-semibold text-white-68 px-4 py-1">Session</p>
                                                    <p className="border-b border-white-12 text-sm font-semibold text-white px-4 py-1">7</p>
                                                    <p className="border-b border-white-12 text-sm font-semibold text-white px-4 py-1">5</p>
                                                    <p className="border-b border-white-12 text-sm font-semibold text-white px-4 py-1">3</p>
                                                    <p className="text-sm font-semibold text-white px-4 py-1">1</p>
                                                </div>
                                                <div className="">
                                                    <p className="border-b border-white-12 text-base font-semibold text-white-68 px-4 py-1">Off</p>
                                                    <p className="border-b border-white-12 text-sm font-semibold text-white px-4 py-1">%15</p>
                                                    <p className="border-b border-white-12 text-sm font-semibold text-white px-4 py-1">%10</p>
                                                    <p className="border-b border-white-12 text-sm font-semibold text-white px-4 py-1">%5</p>
                                                    <p className="text-sm font-semibold text-white px-4 py-1">%0</p>
                                                </div>
                                                <div className="">
                                                    <p className="border-b border-white-12 text-base font-semibold text-white-68 px-4 py-1">Price</p>
                                                    <p className="border-b border-white-12 text-sm font-semibold text-white px-4 py-1">€{(mentor.price - (mentor.price * 0.15)).toFixed(2)}</p>
                                                    <p className="border-b border-white-12 text-sm font-semibold text-white px-4 py-1">€{(mentor.price - (mentor.price * 0.1)).toFixed(2)}</p>
                                                    <p className="border-b border-white-12 text-sm font-semibold text-white px-4 py-1">€{(mentor.price - (mentor.price * 0.05)).toFixed(2)}</p>
                                                    <p className="text-sm font-semibold text-white px-4 py-1">€{mentor.price}</p>
                                                </div>
                                            </div>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                                <p className="hidden lg:block text-white text-base font-normal">
                                    Start from €{mentor.price}
                                </p> */}
                                {/* <div className="flex lg:hidden items-center gap-6">
                                    <Popover>
                                        <PopoverTrigger>
                                            <div
                                                className="flex items-center px-4 py-2.5 cursor-pointer border border-black bg-darker">
                                                <p className="text-white text-base font-semibold mr-1">Fee
                                                    quote </p>
                                                <QuestionIcon/>
                                            </div>
                                        </PopoverTrigger>
                                        <PopoverContent>
                                            <div
                                                className="px-4 py-1 bg-blur-100 flex border rounded-sm border-white-12 -mt-1.5">
                                                <div className="">
                                                    <p className="border-b border-white-12 text-base font-semibold text-white-68 px-4 py-1">Session</p>
                                                    <p className="border-b border-white-12 text-sm font-semibold text-white px-4 py-1">7</p>
                                                    <p className="border-b border-white-12 text-sm font-semibold text-white px-4 py-1">5</p>
                                                    <p className="border-b border-white-12 text-sm font-semibold text-white px-4 py-1">3</p>
                                                    <p className="text-sm font-semibold text-white px-4 py-1">1</p>
                                                </div>
                                                <div className="">
                                                    <p className="border-b border-white-12 text-base font-semibold text-white-68 px-4 py-1">Off</p>
                                                    <p className="border-b border-white-12 text-sm font-semibold text-white px-4 py-1">%15</p>
                                                    <p className="border-b border-white-12 text-sm font-semibold text-white px-4 py-1">%10</p>
                                                    <p className="border-b border-white-12 text-sm font-semibold text-white px-4 py-1">%5</p>
                                                    <p className="text-sm font-semibold text-white px-4 py-1">%0</p>
                                                </div>
                                                <div className="">
                                                    <p className="border-b border-white-12 text-base font-semibold text-white-68 px-4 py-1">Price</p>
                                                    <p className="border-b border-white-12 text-sm font-semibold text-white px-4 py-1">€{(mentor.price - (mentor.price * 0.15)).toFixed(2)}</p>
                                                    <p className="border-b border-white-12 text-sm font-semibold text-white px-4 py-1">€{(mentor.price - (mentor.price * 0.1)).toFixed(2)}</p>
                                                    <p className="border-b border-white-12 text-sm font-semibold text-white px-4 py-1">€{(mentor.price - (mentor.price * 0.05)).toFixed(2)}</p>
                                                    <p className="text-sm font-semibold text-white px-4 py-1">€{mentor.price}</p>
                                                </div>
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                    <p className="text-white text-base font-normal">
                                        Start from €{mentor.price}
                                    </p>
                                </div> */}
                                {/* <div className="mt-5 flex items-center py-2">
                                    <InfoIcon className="w-5 h-5 min-w-5" />
                                    <p className="text-white-68 font-inter text-sm pl-3">If you are residing in 🇮🇷 Iran it's possible to pay in Rial.</p>
                                </div> */}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="absolute bg-gradient-to-b from-white-5 w-full to-white-8 h-[35%] bottom-0">
                </div>
            </div>
            <div className="flex flex-col items-center relative">
                <div className="lg:w-4/5 lg:max-w-[1103px] px-9">
                    <div>
                        <p className="text-white-50 text-base font-normal mt-16">
                            {mentor.about}
                        </p>
                        <ul className="flex items-center mt-5 gap-4">
                            {mentorSocialsName.map((item, index) => {
                                const link = mentorSocialsLink[index];
                                return <li key={index}>
                                    <a href={link} target="_blank">
                                        {item === 'linkedin' ? <LinkedInIcon className="w-6 h-6 stroke-white-30" /> :
                                            <TwitterIcon className="ml-4 w-6 h-6 stroke-white-30" />}
                                    </a>
                                </li>
                            })}
                        </ul>
                    </div>
                    <div className="mt-20">
                        <p className="text-2xl font-bold text-white-30 mb-3">BACKGROUND</p>
                        <table className="table-auto">
                            <tbody>
                                <tr>
                                    <td className="font-normal text-white-50 text-lg space-y-6">Expertise</td>
                                    <td className="hidden lg:flex gap-2 text-lg font-semibold text-white ml-6 py-3">
                                        {mentor.background.expertise}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="lg:hidden flex gap-2 text-lg font-semibold text-white py-3">
                                        {mentor.background.expertise}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="font-normal text-white-50 text-lg space-y-6">Fluent in</td>
                                    <td className="hidden lg:flex gap-2 text-lg font-semibold text-white ml-6 py-3">
                                        {mentor.background.languages.map((item: string, index: number) => {
                                            return `${index > 0 ? ', ' + item : item}`
                                        })}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="lg:hidden flex gap-2 text-lg font-semibold text-white py-3">
                                        {mentor.background.languages.map((item: string, index: number) => {
                                            return `${index > 0 ? ', ' + item : item}`
                                        })}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-20">
                        <p className="text-2xl font-bold text-white-30 mb-3">EXPERIENCES</p>
                        <ul>
                            {mentor.experiences.map((item, index) => <li
                                className={`flex items-start ${index > 0 ? 'mt-10' : ''}`} key={index}>
                                <Image src={`/images/companies/${item.company.split(" ").join("").toLowerCase()}.svg`}
                                    alt={item.company}
                                    className="min-w-[52px] rounded mr-6"
                                    width={52}
                                    height={52} />
                                <div>
                                    <p className="text-lg text-white font-semibold">{item.title}</p>
                                    <div
                                        className="text-lg text-white-68 font-normal flex flex-col lg:flex-row items-start lg:items-center mt-2">
                                        <p>{item.company}</p>
                                        <p className="ml-0 lg:ml-2">{item.start} - {item.end}</p>
                                    </div>
                                    <div className="flex items-center mt-2">
                                        <LocationIcon className="w-5 h-5" />
                                        <p className="text-lg text-white-68 font-normal ml-2">
                                            {item.location}
                                        </p>
                                    </div>
                                </div>
                            </li>)}
                        </ul>
                    </div>
                    {/*<div className="mt-20">*/}
                    {/*    <p className="text-2xl font-bold text-white-30 mb-3">REVIEWS</p>*/}
                    {/*    <div className="mt-6 bg-[#151515] p-7">*/}
                    {/*        <p className="text-white font-barlow text-2xl font-normal">“Expat Pie is an amazing service*/}
                    {/*            and a wonderful mentor. IT is supportive and knowledgeable with extensive. ”</p>*/}
                    {/*        <p className="text-white-30 text-lg font-normal mt-4">Former mentee, currently working*/}
                    {/*            at</p>*/}
                    {/*        <p className="text-white text-lg font-normal mt-2"><span className="font-bold">Tesla</span>*/}
                    {/*            <span className="text-white-30">as a</span> Sr. Product Manager</p>*/}
                    {/*    </div>*/}
                    {/*    <div className="flex justify-center mt-6">*/}
                    {/*        <button className="bg-white-100 px-4 py-2.5 text-darker text-base font-semibold">Load more*/}
                    {/*            (1)*/}
                    {/*        </button>*/}
                    {/*    </div>*/}
                    {/*</div>*/}
                </div>
                {similarMentors && <div className="mt-20 lg:w-4/5 lg:max-w-[1103px]">
                    <p className="text-2xl font-bold text-white mb-3 px-9 lg:px-0">Other mentors</p>
                    <div className=" max-w-[100vw] lg:max-w-full px-9 lg:px-0">
                        <ul className="flex gap-12 mt-12">
                            {similarMentors?.map((item) => (
                                <li key={item.id}>
                                    <Link href={`/mentor/${item.fullName.toLowerCase().split(" ").join("-")}`}>
                                        <div className="relative">
                                            <div className="overflow-hidden">
                                                <Image
                                                    className="hover:scale-110 transition duration-300 ease-in-out"
                                                    src={`/images/mentors/${item.fullName.toLowerCase().split(" ").join("-")}.jpg`}
                                                    alt="mentor"
                                                    width={200}
                                                    height={260}
                                                    quality={75}
                                                    placeholder="blur"
                                                    loading="lazy"
                                                    blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mMUqwcAALEAlw+6NSgAAAAASUVORK5CYII="
                                                />
                                            </div>
                                            <div
                                                className="absolute bg-gradient-to-b from-blur-0 to-blur-100 lg:w-full h-1/5 -bottom-px w-[101%] -left-px lg:left-0">
                                            </div>
                                            <p className="text-32 font-bold font-barlow text-white-90 absolute bottom-0">{item.fullName.split(" ")[0]}</p>
                                        </div>
                                        <div className="text-left">
                                            {/*<p className="text-32 font-bold text-white-90">{item.fullName.split(" ")[0]}</p>*/}
                                            <p className="text-32 font-bold font-barlow text-white-90">{item.fullName.split(" ")[1]}</p>
                                            <p className="text-lg font-normal text-white-50 mt-5">{item.jobTitle}</p>
                                        </div>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>}
            </div>
        </main>
    );
}
